/** Presentation helpers, kept out of the components so they can be tested. */

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  triaged: 'Triaged',
  accepted: 'Accepted',
  in_progress: 'In progress',
  in_review: 'In review',
  released: 'Released',
  verified: 'Verified',
  closed: 'Closed',
  duplicate: 'Duplicate',
  wont_fix: "Won't fix",
  not_reproducible: 'Not reproducible',
  needs_info: 'Needs info',
};

const TYPE_LABELS: Record<string, string> = {
  bug: 'Bug',
  enhancement: 'Enhancement',
  question: 'Question',
  data_issue: 'Data issue',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

/**
 * Relative time, coarse on purpose.
 *
 * "3 days ago" is what a triager needs; a precise timestamp is noise in a list
 * and is available on the detail page for anyone who wants it.
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const seconds = Math.round((now.getTime() - then) / 1000);
  const future = seconds < 0;
  const magnitude = Math.abs(seconds);

  const units: [number, string][] = [
    [60, 'second'],
    [3600, 'minute'],
    [86400, 'hour'],
    [604800, 'day'],
    [2629800, 'week'],
    [31557600, 'month'],
  ];

  if (magnitude < 45) return future ? 'in a moment' : 'just now';

  for (let i = 0; i < units.length; i += 1) {
    const [limit] = units[i]!;
    if (magnitude < limit) {
      const divisor = i === 0 ? 1 : units[i - 1]![0];
      const value = Math.round(magnitude / divisor);
      const name = units[i]![1];
      return phrase(value, name, future);
    }
  }

  return phrase(Math.round(magnitude / 31557600), 'year', future);
}

function phrase(value: number, unit: string, future: boolean): string {
  const plural = value === 1 ? unit : `${unit}s`;
  return future ? `in ${value} ${plural}` : `${value} ${plural} ago`;
}

/** How urgently the SLA needs attention. Drives the colour in the list. */
export function slaState(
  dueAt: string | null,
  breachedAt: string | null,
  now: Date = new Date(),
): 'none' | 'breached' | 'due_soon' | 'ok' {
  if (breachedAt) return 'breached';
  if (!dueAt) return 'none';

  const remaining = new Date(dueAt).getTime() - now.getTime();
  if (Number.isNaN(remaining)) return 'none';
  if (remaining <= 0) return 'breached';
  // Within four hours is close enough that someone should look now.
  if (remaining < 4 * 3600_000) return 'due_soon';
  return 'ok';
}

export function initials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}
